import { Chip, type ChipProps } from '@mui/material';

type Tone = 'default' | 'info' | 'success' | 'warning' | 'error' | 'neutral';

/** Maps any status string to a consistent tone across the app. */
const STATUS_TONE: Record<string, Tone> = {
  active: 'success', approved: 'success', received: 'success', completed: 'success', selected: 'success', delivered: 'success', awarded: 'success', sent: 'info',
  draft: 'neutral', inactive: 'neutral', closed: 'neutral', superseded: 'neutral', planned: 'neutral',
  pending_approval: 'warning', on_hold: 'warning', in_transit: 'info', partially_received: 'warning', rfq: 'info', po_issued: 'info', dispatched: 'info',
  rejected: 'error', cancelled: 'error', blacklisted: 'error', locked: 'error',
  highly_profitable: 'success', profitable: 'success', average: 'info', low_margin: 'warning', loss_making: 'error', no_revenue: 'neutral',
  in_stock: 'success', posted: 'success', installed: 'info', returned: 'warning', reserved: 'warning', scrapped: 'error',
  // AMC / service lifecycle
  healthy: 'success', warning: 'warning', critical: 'error', decommissioned: 'neutral', under_maintenance: 'warning',
  open: 'info', assigned: 'info', accepted: 'info', on_site: 'info', pending_spare: 'warning', resolved: 'success', renewed: 'info', expired: 'error',
  requested: 'info', issued: 'success', defective_returned: 'warning', warranty_replaced: 'info',
};

const TONE_COLOR: Record<Tone, ChipProps['color']> = {
  default: 'default', neutral: 'default', info: 'info', success: 'success', warning: 'warning', error: 'error',
};

interface Props {
  status?: string | null;
  /** Force a tone instead of deriving from the status. */
  tone?: Tone;
  size?: ChipProps['size'];
}

function label(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Consistent, theme-aware status pill. */
export function StatusBadge({ status, tone, size = 'small' }: Props) {
  if (!status) return <span>—</span>;
  const t = tone ?? STATUS_TONE[status] ?? 'default';
  const color = TONE_COLOR[t];
  return (
    <Chip
      size={size}
      label={label(status)}
      color={color}
      variant={t === 'neutral' || t === 'default' ? 'outlined' : 'filled'}
      sx={t === 'neutral' || t === 'default' ? undefined : { '& .MuiChip-label': { px: 1 } }}
    />
  );
}
